import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { IndustryUiConfig, IndustryFeatureFlags, IndustryTerminology, DealContactRole, PipelineStageTemplate } from '../types';
import { AppMode } from './AppModeContext';

const DEFAULT_TERMINOLOGY: Required<IndustryTerminology> = {
  deal: 'Deal',
  deals: 'Deals',
  pipeline: 'Pipeline',
  stakeholder: 'Stakeholders',
  champion: 'Champions',
  value: 'Deal Value',
  contact: 'Contact',
  contacts: 'Contacts',
  company: 'Company',
};

const DEFAULT_DEAL_CONTACT_ROLES: DealContactRole[] = [
  { key: 'primary', label: 'Primary Contact', color: '#3b82f6' },
  { key: 'stakeholder', label: 'Stakeholder', color: '#6b7280' },
  { key: 'champion', label: 'Champion', color: '#f59e0b' },
];

interface IndustryConfigContextType {
  /** The raw industry config, or null if no industry is assigned */
  industryId: string | null;
  industryName: string | null;
  /** The industry slug (e.g. 'mortgage', 'real-estate'), or null */
  industrySlug: string | null;
  uiConfig: IndustryUiConfig;
  featureFlags: IndustryFeatureFlags;
  pipelineStages: {
    sales: PipelineStageTemplate[];
    recruiting: PipelineStageTemplate[];
  };
  /** Check if a nav item key is visible */
  isNavVisible: (key: string) => boolean;
  /** Check if a form field/section is visible for a given entity */
  isFormFieldVisible: (entity: string, key: string) => boolean;
  /** Check if an opt-in form section is enabled (default-false — only true when explicitly set) */
  isFormSectionEnabled: (entity: string, key: string) => boolean;
  /** Get the list of modes available to this industry */
  availableModes: AppMode[];
  /** Check if a feature flag is enabled */
  isFeatureEnabled: (flag: keyof IndustryFeatureFlags) => boolean;
  /** Whether an industry is configured (false = show everything) */
  hasIndustry: boolean;
  /** Resolved terminology (industry overrides merged with defaults) */
  terminology: Required<IndustryTerminology>;
  /** Deal contact roles for this industry */
  dealContactRoles: DealContactRole[];
}

const IndustryConfigContext = createContext<IndustryConfigContextType | undefined>(undefined);

export const useIndustryConfig = () => {
  const context = useContext(IndustryConfigContext);
  if (!context) {
    throw new Error('useIndustryConfig must be used within IndustryConfigProvider');
  }
  return context;
};

/** Convenience hook: returns true if the nav item with the given key should be visible */
export const useNavVisible = (key: string): boolean => {
  const { isNavVisible } = useIndustryConfig();
  return isNavVisible(key);
};

/** Convenience hook: returns true if the form field should be visible */
export const useFormFieldVisible = (entity: string, key: string): boolean => {
  const { isFormFieldVisible } = useIndustryConfig();
  return isFormFieldVisible(entity, key);
};

/** Convenience hook: returns true if an opt-in form section is enabled */
export const useFormSectionEnabled = (entity: string, key: string): boolean => {
  const { isFormSectionEnabled } = useIndustryConfig();
  return isFormSectionEnabled(entity, key);
};

/** Convenience hook: returns the list of modes this industry allows */
export const useAvailableModes = (): AppMode[] => {
  const { availableModes } = useIndustryConfig();
  return availableModes;
};

/** Convenience hook: returns resolved terminology with defaults */
export const useTerminology = (): Required<IndustryTerminology> => {
  const { terminology } = useIndustryConfig();
  return terminology;
};

/** Convenience hook: returns deal contact roles for this industry */
export const useDealContactRoles = (): DealContactRole[] => {
  const { dealContactRoles } = useIndustryConfig();
  return dealContactRoles;
};

/** Convenience hook: returns the industry slug (e.g. 'mortgage', 'real-estate') */
export const useIndustrySlug = (): string | null => {
  const { industrySlug } = useIndustryConfig();
  return industrySlug;
};

const ALL_MODES: AppMode[] = ['sales', 'recruiting', 'onboarding'];

interface IndustryConfigProviderProps {
  children: ReactNode;
}

export const IndustryConfigProvider: React.FC<IndustryConfigProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const value = useMemo<IndustryConfigContextType>(() => {
    const industry = user?.organization?.industry;
    const hasIndustry = !!industry;

    const uiConfig: IndustryUiConfig = industry?.uiConfig || {};
    const featureFlags: IndustryFeatureFlags = industry?.defaultFeatureFlags || {};
    const pipelineStages = {
      sales: industry?.defaultPipelineStages?.sales || [],
      recruiting: industry?.defaultPipelineStages?.recruiting || [],
    };

    // If no industry is assigned, everything is visible/enabled
    const isNavVisible = (key: string): boolean => {
      if (!hasIndustry) return true;
      const navItems = uiConfig.navItems;
      if (!navItems || Object.keys(navItems).length === 0) return true;
      // If the key isn't in the config at all, default to visible
      return navItems[key] !== false;
    };

    const isFormFieldVisible = (entity: string, key: string): boolean => {
      if (!hasIndustry) return true;
      const sections = uiConfig.formSections;
      if (!sections || !sections[entity]) return true;
      return sections[entity][key] !== false;
    };

    // Default-false: only returns true when explicitly set to true in config
    const isFormSectionEnabled = (entity: string, key: string): boolean => {
      if (!hasIndustry) return false;
      const sections = uiConfig.formSections;
      if (!sections || !sections[entity]) return false;
      return sections[entity][key] === true;
    };

    const availableModes: AppMode[] = (() => {
      if (!hasIndustry) return ALL_MODES;
      const configured = uiConfig.availableModes;
      if (!configured || configured.length === 0) return ALL_MODES;
      return configured;
    })();

    const isFeatureEnabled = (flag: keyof IndustryFeatureFlags): boolean => {
      if (!hasIndustry) return true;
      // If flag is not set, default to enabled
      return featureFlags[flag] !== false;
    };

    // Merge industry terminology with defaults
    const terminology: Required<IndustryTerminology> = {
      ...DEFAULT_TERMINOLOGY,
      ...(uiConfig.terminology || {}),
    };

    // Get deal contact roles from industry config, or use defaults
    const dealContactRoles: DealContactRole[] =
      uiConfig.dealContactRoles && uiConfig.dealContactRoles.length > 0
        ? uiConfig.dealContactRoles
        : DEFAULT_DEAL_CONTACT_ROLES;

    return {
      industryId: industry?.id || null,
      industryName: industry?.name || null,
      industrySlug: industry?.slug || null,
      uiConfig,
      featureFlags,
      pipelineStages,
      isNavVisible,
      isFormFieldVisible,
      isFormSectionEnabled,
      availableModes,
      isFeatureEnabled,
      hasIndustry,
      terminology,
      dealContactRoles,
    };
  }, [user?.organization?.industry]);

  return (
    <IndustryConfigContext.Provider value={value}>
      {children}
    </IndustryConfigContext.Provider>
  );
};

export default IndustryConfigContext;
